import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Control, useController, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, FileInput } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import Resource from '@/models/Resource';
import { getFileExtensionFromUrl } from '@/utils/file';

import { ResourceFormFields, ResourceTypeEnum } from './ResourceInlineForm';

type ResourceFileFieldProps = {
  control: Control<ResourceFormFields>;
  resource?: Resource;
};

export default function ResourceFileField({
  control,
  resource,
}: ResourceFileFieldProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.resourceForm.form',
  });

  const typeValue = useWatch({ control, name: 'type' });

  const { field, fieldState } = useController({
    control,
    name: 'resourceFile',
    rules: {
      validate: file => {
        if (typeValue !== ResourceTypeEnum.FILE) return true;
        if (file instanceof File) return true;
        if (resource?.resourceFile) return true;
        return t('fileRequired');
      },
    },
  });

  const existingFileUrl = resource?.resourceFile ?? null;
  const existingFileExtension = existingFileUrl
    ? getFileExtensionFromUrl(existingFileUrl)
    : '';

  return (
    <Fieldset
      className="flex-1"
      label={t('file')}
      labelAlt={fieldState.error?.message}
    >
      <div className="flex items-center gap-2">
        <FileInput
          ref={field.ref}
          name={field.name}
          onBlur={field.onBlur}
          error={!!fieldState.error}
          className="flex-1 min-w-0"
          onChange={event =>
            field.onChange(event.target.files?.[0] ?? null)
          }
        />
        <ConditionalRenderer condition={existingFileUrl}>
          <a
            href={existingFileUrl ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button type="button" size="sm">
              {existingFileExtension
                ? `${t('currentFile')} (${existingFileExtension})`
                : t('currentFile')}
              <ArrowTopRightOnSquareIcon className="w-3 h-3 shrink-0" />
            </Button>
          </a>
        </ConditionalRenderer>
      </div>
    </Fieldset>
  );
}
